import styled from "styled-components";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { calcRem } from "styles/theme";

type Props = {
  tooltip: string | React.ReactNode;
  children?: JSX.Element;
  showAtTop?: boolean;
  left?: boolean;
  right?: boolean;
  style?: React.CSSProperties;
  hideArrow?: boolean;
};

const TooltipContainer = styled.div`
  position: fixed;
  background: rgba(0, 0, 0, 0.95);
  color: white;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: ${calcRem(12)};
  z-index: 1040;
  line-height: 1.25;
  pointer-events: none;
  margin: 10px 0;
`;

const TooltipArrow = styled.span`
  width: 0;
  height: 0;
  border: solid transparent;
  border-width: 8px 4px;
  position: absolute;
  transform: translateX(-50%);
`;

export function WithTooltip({
  tooltip,
  children,
  showAtTop,
  left,
  right,
  style = {},
  hideArrow,
}: Props) {
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  let placement = "";
  if (showAtTop) {
    if (right) {
      placement = "topRight";
    } else if (left) {
      placement = "topLeft";
    } else {
      placement = "topCenter";
    }
  } else {
    if (right) {
      placement = "bottomRight";
    } else if (left) {
      placement = "bottomLeft";
    } else {
      placement = "bottomCenter";
    }
  }

  const calculatePosition = () => {
    if (!tooltipRef.current) return;
    let position: Record<string, string> = {};
    const rect = tooltipRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current.offsetHeight;
    const tooltipWidth = tooltipRef.current.offsetWidth;

    const top = placement.startsWith("top")
      ? rect.top - tooltipHeight - rect.height - 10
      : rect.bottom;
    const left = placement.endsWith("Left")
      ? rect.left - tooltipWidth
      : placement.endsWith("Right")
      ? rect.right
      : rect.left + tooltipWidth / 2;

    position = { top: `${top}px`, left: `${left}px` };
    if (placement.endsWith("Center")) {
      position.transform = `translateX(-50%)`;
    } else if (placement.endsWith("Right")) {
      position.transform = `translateX(calc(-100% + 30px))`;
    }
    return position;
  };

  const tooltipPosition = () => {
    let position: Record<string, string> = {};
    if (placement.startsWith("top")) {
      position.top = "100%";
      position.borderTopColor = style?.backgroundColor || "rgba(0, 0, 0, 0.95)";
    }
    if (placement.startsWith("bottom")) {
      position.bottom = "100%";
      position.borderBottomColor =
        style?.backgroundColor || "rgba(0, 0, 0, 0.95)";
    }
    if (placement.endsWith("Left")) {
      position.left = "30px";
    } else if (placement.endsWith("Right")) {
      position.right = "30px";
    } else {
      position.left = "50%";
      position.transform = "translateX(-50%)";
    }
    return position;
  };

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <span
        className="with-toolTip"
        ref={tooltipRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchEnd={handleMouseLeave}
        onClick={handleMouseLeave}
        data-testid="blocks-with-tooltip-tsx-blocks-span-mouse-leave"
      >
        {children}
      </span>
      {isVisible &&
        createPortal(
          <TooltipContainer style={{ ...calculatePosition(), ...style }}>
            {!hideArrow && <TooltipArrow style={tooltipPosition()} />}
            {tooltip}
          </TooltipContainer>,
          document.body
        )}
    </>
  );
}
